import { useTranslation } from 'react-i18next';
import { Loader2, Search, Package } from 'lucide-react';
import { useCan } from '../../../lib/rbac';

function DiscoveryResultsTable({
  items,
  selectedAsins,
  toggleAsin,
  loading,
  emptyMessage,
  allowQueueDuplicates = false,
}: {
  items: any[];
  selectedAsins: Set<string>;
  toggleAsin: (asin: string) => void;
  loading: boolean;
  emptyMessage?: string;
  /** Modo stock propio: permite re-seleccionar ASINs que ya están en cola/borradores
   *  (típico: apartados por sin stock) — los publicados de verdad siguen bloqueados. */
  allowQueueDuplicates?: boolean;
}) {
  const { t } = useTranslation();
  const canMoney = useCan()('global.view_money');
  const isSelectable = (i: any) =>
    allowQueueDuplicates ? !i.already_published : !i.is_duplicate && !i.already_published && !i.in_drafts;

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-12 text-[var(--color-text-tertiary)]">
        <Search size={32} className="mx-auto mb-2 opacity-30" />
        <p className="text-sm">{emptyMessage || t('pub.feed.noResults')}</p>
      </div>
    );
  }

  const selectAll = () => {
    const selectableAsins = items.filter(isSelectable).map(i => i.asin);
    const allSelected = selectableAsins.every(a => selectedAsins.has(a));
    if (allSelected) {
      selectableAsins.forEach(a => toggleAsin(a));
    } else {
      selectableAsins.filter(a => !selectedAsins.has(a)).forEach(a => toggleAsin(a));
    }
  };

  return (
    <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            <th className="text-left px-3 py-3">
              <input
                type="checkbox"
                onChange={selectAll}
                checked={items.filter(isSelectable).length > 0 &&
                  items.filter(isSelectable).every(i => selectedAsins.has(i.asin))}
                className="rounded border-[var(--color-border)]"
              />
            </th>
            <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">Img</th>
            <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">ASIN</th>
            <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('common.name')}</th>
            <th className="text-right px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('common.price')}</th>
            <th className="text-right px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('pub.feed.monthlySold')}</th>
            <th className="text-right px-3 py-3 text-[var(--color-text-secondary)] font-medium">BSR</th>
            <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('common.status')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const isDuplicate = !isSelectable(item);
            return (
              <tr
                key={item.asin}
                className={`border-b border-[var(--color-border)] transition-colors ${
                  isDuplicate ? 'opacity-50' : 'hover:bg-[var(--color-bg-tertiary)]'
                }`}
              >
                <td className="px-3 py-2.5">
                  <input
                    type="checkbox"
                    checked={selectedAsins.has(item.asin)}
                    onChange={() => toggleAsin(item.asin)}
                    disabled={isDuplicate}
                    className="rounded border-[var(--color-border)]"
                  />
                </td>
                <td className="px-3 py-2.5">
                  {item.image || item.main_image || item.thumbnail ? (
                    <img src={item.image || item.main_image || item.thumbnail} alt="" className="w-[60px] h-[60px] rounded object-cover" />
                  ) : (
                    <div className="w-[60px] h-[60px] rounded bg-[var(--color-bg-tertiary)] flex items-center justify-center">
                      <Package size={18} className="text-[var(--color-text-tertiary)]" />
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <span className="font-mono text-xs text-teal-500 font-medium">{item.asin}</span>
                </td>
                <td className="px-3 py-2.5 text-[var(--color-text)]">
                  {item.title || '---'}
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-[var(--color-text)]">
                  {canMoney && item.price != null ? `$${Number(item.price).toFixed(2)}` : '\u2014'}
                </td>
                <td className="px-3 py-2.5 text-right text-[var(--color-text)]">
                  {item.monthly_sold != null ? Number(item.monthly_sold).toLocaleString() : '---'}
                </td>
                <td className="px-3 py-2.5 text-right text-[var(--color-text)]">
                  {item.bsr != null ? `#${Number(item.bsr).toLocaleString()}` : '---'}
                </td>
                <td className="px-3 py-2.5">
                  {item.already_published ? (
                    <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs">{t('pub.feed.alreadyPublished')}</span>
                  ) : item.in_drafts ? (
                    <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded text-xs">{t('pub.feed.inDrafts')}</span>
                  ) : item.is_duplicate ? (
                    <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded text-xs">{t('pub.feed.duplicates')}</span>
                  ) : (
                    <span className="px-2 py-0.5 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400 rounded text-xs">{t('pub.feed.newAsins')}</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export { DiscoveryResultsTable };
